<script setup>
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const isHovered = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};
</script>

<template>
  <div
    class="flow-node min-w-[180px] max-w-[240px] min-h-[80px] bg-white dark:bg-n-slate-1 rounded-[10px] p-2 text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-green-500 shadow-lg'
        : 'border border-transparent hover:border-n-blue-8'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      </div>
    </NodeToolbar>
    <Handle
      type="target"
      :position="Position.Left"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_INPUT')"
    />
    <div class="flex items-center gap-2">
      <div
        class="w-7 h-7 rounded-lg flex items-center justify-center bg-n-violet-1 dark:bg-n-violet-2 shrink-0"
      >
        <span class="i-lucide-clipboard-list text-sm text-n-violet-11" />
      </div>
      <span class="font-semibold text-n-slate-12 text-[9px]">
        {{ t('FLOW_BUILDER.SIDEBAR.DATA_COLLECTION') }}
      </span>
    </div>
    <div
      v-if="props.data?.prompt_message"
      class="mt-1.5 px-1.5 py-1 rounded bg-n-slate-1 dark:bg-n-slate-2 text-[7px] text-n-slate-11 line-clamp-2"
    >
      {{ props.data.prompt_message }}
    </div>
    <div
      v-if="props.data?.timeout_value"
      class="mt-1 flex items-center gap-1 text-[7px] text-n-slate-11"
    >
      <span class="i-lucide-timer text-[8px]" />
      {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.WAIT_LABEL') }}:
      {{ props.data.timeout_value }}
      {{
        t(
          `FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.UNITS.${(props.data?.timeout_unit || 'minutes').toUpperCase()}`
        )
      }}
    </div>
    <div class="flex justify-between w-full mt-2 px-1 pb-1">
      <span class="text-[5px] text-n-teal-9 font-medium text-left flex-1">
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.OUTPUT_DEFAULT') }}
      </span>
      <span class="text-[5px] text-n-amber-9 font-medium text-right flex-1">
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.OUTPUT_TIMEOUT') }}
      </span>
    </div>
    <Handle
      id="default"
      type="source"
      :position="Position.Right"
      class="!bg-n-teal-9 !w-1.5 !h-1.5 !border !border-white !top-[30%]"
      :title="t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.OUTPUT_DEFAULT')"
    />
    <Handle
      id="timeout"
      type="source"
      :position="Position.Right"
      class="!bg-n-amber-9 !w-1.5 !h-1.5 !border !border-white !top-[70%]"
      :title="t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.OUTPUT_TIMEOUT')"
    />
  </div>
</template>
